import { OptionsPopover, type OptionsPopoverOption, type OptionsPopoverSection, ScoreSelect } from "@foodpilot/foods";
import { Popover, useTheme } from "@mui/material";
import { useId, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { useEntityScores } from "src/context/Scores/useEntityScores";

const MENU_MIN_WIDTH_SPACING = 37;

export const ScoreSelectHeader = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const menuId = useId();
    const { scoresWithVariations, lastSelectedVariation, selectVariation } = useEntityScores();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    if (scoresWithVariations.length === 0) {
        return null;
    }

    const closeMenu = () => setAnchor(null);

    const sections = scoresWithVariations.reduce<OptionsPopoverSection[]>((acc, score) => {
        const option = { id: score.rowId, label: score.label, disabled: score.disabled };
        const section = acc.find(({ header }) => header === score.group);

        if (section === undefined) {
            acc.push({ header: score.group, options: [option] });
        } else {
            section.options.push(option);
        }

        return acc;
    }, []);

    const handleChange = (option: OptionsPopoverOption) => {
        const picked = scoresWithVariations.find(({ rowId }) => rowId === option.id);

        if (picked === undefined) {
            return;
        }

        selectVariation(picked.scoreId, picked.variationId);
        closeMenu();
    };

    const heading = {
        title: lastSelectedVariation.group,
        subtitle: lastSelectedVariation.label,
    };

    if (scoresWithVariations.length === 1) {
        return <ScoreSelect {...heading} />;
    }

    return (
        <>
            <ScoreSelect
                {...heading}
                actionAriaLabel={t("product.v2.scores.overview.change_variation", "Change {{score}} variation", {
                    score: lastSelectedVariation.group,
                })}
                actionControls={menuId}
                onOpen={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
                open={anchor !== null}
            />
            <Popover
                anchorEl={anchor}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                elevation={0}
                id={menuId}
                onClose={closeMenu}
                open={anchor !== null}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: theme.spacing(1.5),
                            boxShadow: theme.custom.shadows.popover,
                        },
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
            >
                <OptionsPopover
                    content={sections}
                    defaultIndex={lastSelectedVariation.rowId}
                    minWidth={theme.spacing(MENU_MIN_WIDTH_SPACING)}
                    onChange={handleChange}
                />
            </Popover>
        </>
    );
};
